import { Fragment, useEffect, useMemo, useState } from 'react'
import { useSpring, animated } from '@react-spring/web'

import dynamic from 'next/dynamic'

import appConfig from '~/config'

import { useAppSelector } from '~/redux/hooks'

import { isAuthenticated } from '~/auth/slices/auth.slice'

import ChestLoader from './components/ChestLoader'
import ChestSpriteImages from './components/ChestSpriteImages'
import { ChestSize } from './types'
import { IMAGES_COUNT, SPRING_CONFIG, SPRITE_COLS, SPRITE_ROWS } from './constants'
import { getSpriteSize } from './helpers'

const PointsGained = dynamic(() => import('~/components/PointsGained'), { ssr: false })
const OrbsGained = dynamic(() => import('./components/OrbsGained/OrbsGained'), { ssr: false })

const AUDIO_URL = `${appConfig.ASSETS_BUCKET}audios/chest-open.mp3`

export default function Chest({
  awardOrbs,
  awardPoints,
  didCollectAward,
  onAnimationDidEnd,
  onClick,
  size = 'x-small',
}: {
  awardOrbs?: number
  awardPoints?: number
  didCollectAward: boolean
  onAnimationDidEnd: () => void
  onClick?: () => void
  size?: ChestSize
}): React.ReactElement {
  const [didOpenChest, setDidOpenChest] = useState(false)
  const [executeAnimation, setExecuteAnimation] = useState(false)
  const [frame, setFrame] = useState(didCollectAward ? IMAGES_COUNT : 1)
  const [spriteImageDidLoad, setSpriteImageDidLoad] = useState(false)
  const isInitialFrame = frame === 1
  const authenticated = useAppSelector(isAuthenticated)
  const spring = useSpring(spriteImageDidLoad && !didCollectAward ? SPRING_CONFIG : {})
  const audio = useMemo(
    () => (typeof window === 'undefined' || !authenticated ? null : new Audio(AUDIO_URL)),
    [authenticated],
  )

  const [spriteWidth, spriteHeight] = getSpriteSize(size)
  const imageHeight = spriteHeight / SPRITE_ROWS
  const imageWidth = spriteWidth / SPRITE_COLS

  useEffect(() => {
    const handleOnAnimationDidEnd = (): void => {
      setDidOpenChest(true)
      onAnimationDidEnd()
    }

    if (isInitialFrame && executeAnimation) {
      audio?.play()

      const interval = setInterval(() => {
        setFrame((prevState) => {
          if (prevState === IMAGES_COUNT) {
            handleOnAnimationDidEnd()
            clearInterval(interval)
            return prevState
          } else {
            return prevState + 1
          }
        })
      }, 35)
    }
  }, [audio, executeAnimation, isInitialFrame, onAnimationDidEnd])

  const handleOnClick = (): void => {
    if (!didCollectAward && !didOpenChest) {
      setExecuteAnimation(true)
      onClick?.()
    }
  }
  return (
    <Fragment>
      <ChestLoader
        containerHeight={imageHeight}
        containerWidth={imageWidth}
        displayLoader={!spriteImageDidLoad}
        size={size}
      />
      <animated.div
        className={`relative ml-4 -mt-4 mb-4 ${spriteImageDidLoad ? 'block' : 'hidden'}`}
        data-test-id="reward-chest"
        onClick={handleOnClick}
        style={{ minWidth: imageWidth, minHeight: imageHeight, ...spring }}
      >
        <ChestSpriteImages
          className="absolute max-w-none !m-0"
          frame={frame}
          imageHeight={imageHeight}
          imageWidth={imageWidth}
          setSpriteImageDidLoad={setSpriteImageDidLoad}
          spriteCols={SPRITE_COLS}
          spriteHeight={spriteHeight}
          spriteImageDidLoad={spriteImageDidLoad}
          spriteRows={SPRITE_ROWS}
          spriteWidth={spriteWidth}
        />
        <div className="absolute z-10">
          {didOpenChest && typeof awardPoints === 'number' && awardPoints > 0 && (
            <PointsGained scoreText={`${String(awardPoints)} XP`} />
          )}
          {didOpenChest && typeof awardOrbs === 'number' && awardOrbs > 0 && (
            <div className={`flex flex-col ${awardOrbs > 1 ? '-ml-10' : ''}`}>
              <div className="flex justify-center">
                <OrbsGained size={size} />
                {awardOrbs > 1 && <OrbsGained size={size} />}
                {awardOrbs > 2 && <OrbsGained size={size} />}
              </div>
              <div className="flex justify-center">
                {awardOrbs > 3 && <OrbsGained size={size} />}
                {awardOrbs > 4 && <OrbsGained size={size} />}
              </div>
            </div>
          )}
        </div>
      </animated.div>
    </Fragment>
  )
}
